import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { DataBusStatus } from 'src/api/models';
import { DataBusService } from 'src/api/services';

@Component({
  selector: 'app-databus-health',
  templateUrl: './databus-health.component.html',
  styleUrls: ['./databus-health.component.css']
})
export class DatabusHealthComponent implements OnInit {

  // create a date value that is 1 hour older than the current time
  public oneHourAgo: Date = new Date(new Date().getTime() - 3600000);
  public inOneMonth: Date = new Date(new Date().getTime() + 3600000*24*30);
  public inTwoMonth: Date = new Date(new Date().getTime() - 3600000*24*60);
  
  // add method to convert string date to Date object
  public convertToDate(dateString: string): Date {
    return new Date(dateString);
  }

  constructor(
    private dataBusService: DataBusService,
    private toast: ToastrService,
    private route: Router) {}

  statuses : DataBusStatus[];

  // Sorting state for health table
  sortColumnHealth: string = '';
  sortDirectionHealth: 'asc' | 'desc' = 'asc';

  // 30 minutes ago threshold for stale instances
  public thirtyMinutesAgo: Date = new Date(new Date().getTime() - 1800000);

  // Selected status for viewing error details
  selectedErrorStatus: DataBusStatus = null;

  ngOnInit(): void {
    this.route.routeReuseStrategy.shouldReuseRoute = () => false;
    this.loadStatuses();
  }

  loadStatuses(): void {
    this.dataBusService.DataBusGetDataBusStatusList().subscribe(s => {
      this.statuses = s;    
    });
  }

  // Health Dashboard: instances with issues
  get unhealthyInstances(): DataBusStatus[] {
    if (!this.statuses) return [];
    const now = new Date();
    return this.sortInstances(
      this.statuses.filter(s => {
        const isExpired = s.expiry && this.convertToDate(s.expiry) < now;
        const isStale = this.convertToDate(s.timeStamp) < this.thirtyMinutesAgo;
        const hasErrors = s.errorCount > 0;
        return isExpired || isStale || hasErrors;
      }),
      this.sortColumnHealth,
      this.sortDirectionHealth
    );
  }

  // Health statistics
  get expiredCount(): number {
    if (!this.statuses) return 0;
    const now = new Date();
    return this.statuses.filter(s => s.expiry && this.convertToDate(s.expiry) < now).length;
  }

  get staleCount(): number {
    if (!this.statuses) return 0;
    return this.statuses.filter(s => this.convertToDate(s.timeStamp) < this.thirtyMinutesAgo).length;
  }

  get errorInstanceCount(): number {
    if (!this.statuses) return 0;
    return this.statuses.filter(s => s.errorCount > 0).length;
  }

  get healthyCount(): number {
    if (!this.statuses) return 0;
    const now = new Date();
    return this.statuses.filter(s => {
      const isExpired = s.expiry && this.convertToDate(s.expiry) < now;
      const isStale = this.convertToDate(s.timeStamp) < this.thirtyMinutesAgo;
      const hasErrors = s.errorCount > 0;
      return !isExpired && !isStale && !hasErrors;
    }).length;
  }

  // Check if instance has specific health issues
  isExpired(status: DataBusStatus): boolean {
    if (!status.expiry) return false;
    return this.convertToDate(status.expiry) < new Date();
  }

  isStale(status: DataBusStatus): boolean {
    return this.convertToDate(status.timeStamp) < this.thirtyMinutesAgo;
  }

  hasErrors(status: DataBusStatus): boolean {
    return status.errorCount > 0;
  }

  // Show error details modal
  showErrorDetails(status: DataBusStatus): void {
    this.selectedErrorStatus = status;
  }

  // Sort instances by column
  sortInstances(instances: DataBusStatus[], column: string, direction: 'asc' | 'desc'): DataBusStatus[] {
    if (!column) return instances;

    return [...instances].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch(column) {
        case 'computerName':
          aValue = a.computerName?.toLowerCase() || '';
          bValue = b.computerName?.toLowerCase() || '';
          break;
        case 'customerName':
          aValue = a.customerName?.toLowerCase() || '';
          bValue = b.customerName?.toLowerCase() || '';
          break;
        case 'expiry':
          aValue = a.expiry ? new Date(a.expiry).getTime() : 0;
          bValue = b.expiry ? new Date(b.expiry).getTime() : 0;
          break;
        case 'timeStamp':
          aValue = new Date(a.timeStamp).getTime();
          bValue = new Date(b.timeStamp).getTime();
          break;
        case 'version':
          aValue = a.version?.toLowerCase() || '';
          bValue = b.version?.toLowerCase() || '';
          break;
        case 'errorCount':
          aValue = a.errorCount;
          bValue = b.errorCount;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Sort handler for health table
  sortTable(column: string): void {
    if (this.sortColumnHealth === column) {
      this.sortDirectionHealth = this.sortDirectionHealth === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumnHealth = column;
      this.sortDirectionHealth = 'asc';
    }
  }

  // Get sort indicator icon
  getSortIcon(column: string): string {
    if (this.sortColumnHealth !== column) return '';
    return this.sortDirectionHealth === 'asc' ? '▲' : '▼';
  }

  deleteStatus(db: DataBusStatus){
    this.dataBusService.DataBusDeleteDataBusStatus(db.dataBusStatusId).subscribe(r => {
      this.toast.success("Status Removed");
      // remove db from this.statuses
      this.statuses = this.statuses.filter(s => s.dataBusStatusId !== db.dataBusStatusId);
    }, err => {
      this.toast.error("Error removing status");
    })
  }
}
